<template>
	<section
		v-if="hasProfile"
		ref="profileSectionRef"
		class="teacher-profile"
		:class="{
			'teacher-profile--area': isAreaTeacher && areaHeaderImage,
		}"
	>
		<h1 class="sr-only">{{ t(`${I18N_NS}.teacherProfile`) }}</h1>

		<!-- 普通老师 -->
		<template v-if="!isAreaTeacher">
			<div class="teacher-profile__header">
				<div class="teacher-profile__avatar-wrap">
					<img class="teacher-profile__avatar" :src="avatarSrc" :alt="profileName" />
				</div>

				<div class="teacher-profile__main">
					<div class="teacher-profile__top">
						<div class="teacher-profile__identity">
							<div class="teacher-profile__name">{{ profileName }}</div>
							<ul v-if="profileTagItems.length" class="teacher-profile__tags" role="list">
								<li
									v-for="tag in profileTagItems"
									:key="tag.text"
									class="teacher-profile__tag"
									:style="getProfileTagStyle(tag)"
								>
									{{ tag.text }}
								</li>
							</ul>
						</div>

						<button
							v-if="chatTeacherId"
							type="button"
							class="teacher-profile__chat-btn"
							@click="toChat"
						>
							{{ chatButtonText }}
						</button>
					</div>

					<dl class="teacher-profile__stats">
						<div class="teacher-profile__stat">
							<dt>{{ t(`${I18N_NS}.yearsExperience`) }}</dt>
							<dd>{{ displayYear }}</dd>
						</div>
						<div class="teacher-profile__stat">
							<dt>{{ t(`${I18N_NS}.serviceCount`) }}</dt>
							<dd>{{ displayAnswers }}</dd>
						</div>
						<div class="teacher-profile__stat">
							<dt>{{ t(`${I18N_NS}.rating`) }}</dt>
							<dd>{{ displayScore }}</dd>
						</div>
					</dl>
				</div>
			</div>

			<p v-if="profileIntro" class="teacher-profile__intro">
				{{ profileIntro }}
			</p>
		</template>

		<!-- 区域老师：有头图 -->
		<div
			v-else-if="areaHeaderImage"
			class="teacher-profile__header teacher-profile__header--area-image"
		>
			<picture class="teacher-profile__area-picture">
				<source
					v-if="areaMobileHeaderImage"
					media="(max-width: 900px)"
					:srcset="areaMobileHeaderImage"
				/>
				<img class="teacher-profile__area-image" :src="areaHeaderImage" :alt="profileName" />
			</picture>

			<div class="teacher-profile__main">
				<div class="teacher-profile__top">
					<div class="teacher-profile__identity">
						<div class="teacher-profile__name">{{ profileName }}</div>
						<ul v-if="profileTagItems.length" class="teacher-profile__tags" role="list">
							<li
								v-for="tag in profileTagItems"
								:key="tag.text"
								class="teacher-profile__tag"
								:style="getProfileTagStyle(tag)"
							>
								{{ tag.text }}
							</li>
						</ul>
					</div>

					<button
						v-if="chatTeacherId"
						type="button"
						class="teacher-profile__chat-btn"
						@click="toChat"
					>
						{{ chatButtonText }}
					</button>
				</div>

				<p v-if="profileIntro" class="teacher-profile__intro">
					{{ profileIntro }}
				</p>
			</div>
		</div>

		<!-- 区域老师：无头图（与普通老师同布局，stats 位置留白） -->
		<template v-else>
			<div class="teacher-profile__header">
				<div class="teacher-profile__avatar-wrap">
					<img class="teacher-profile__avatar" :src="avatarSrc" :alt="profileName" />
				</div>

				<div class="teacher-profile__main">
					<div class="teacher-profile__top">
						<div class="teacher-profile__identity">
							<div class="teacher-profile__name">{{ profileName }}</div>
							<ul v-if="profileTagItems.length" class="teacher-profile__tags" role="list">
								<li
									v-for="tag in profileTagItems"
									:key="tag.text"
									class="teacher-profile__tag"
									:style="getProfileTagStyle(tag)"
								>
									{{ tag.text }}
								</li>
							</ul>
						</div>

						<button
							v-if="chatTeacherId"
							type="button"
							class="teacher-profile__chat-btn"
							@click="toChat"
						>
							{{ chatButtonText }}
						</button>
					</div>

					<div
						class="teacher-profile__stats teacher-profile__stats--spacer"
						aria-hidden="true"
					></div>
				</div>
			</div>

			<p v-if="profileIntro" class="teacher-profile__intro">
				{{ profileIntro }}
			</p>
		</template>

		<div ref="privacyBannerRef" class="teacher-profile__privacy">
			<div class="teacher-profile__privacy-text">
				<strong>{{ t(`${I18N_NS}.privacyTitle`) }}</strong>
				<span>{{ t(`${I18N_NS}.privacyDesc`) }}</span>
			</div>
			<img class="teacher-profile__privacy-check" :src="secureIcon" alt="" aria-hidden="true" />
		</div>
	</section>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultAvatar from "~/assets/images/default_avatar.png";
import secureIcon from "../../images/secure.png";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const openLogin = inject("openLogin");
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Object,
		default: () => ({}),
	},
});

const profileSectionRef = ref(null);
const privacyBannerRef = ref(null);
const hasProfile = computed(() => Boolean(props.data && Object.keys(props.data).length));
const isAreaTeacher = computed(() => Number(props.data?.area_type) === 10);

watch(
	() => props.data,
	data => {
		console.log("[导师详情teacher-info] props.data:", data);
		console.log("[导师详情teacher-info] derived:", {
			hasProfile: Boolean(data && Object.keys(data || {}).length),
			isAreaTeacher: Number(data?.area_type) === 10,
			areaHeaderImage: Number(data?.area_type) === 10 ? data?.big_teacher_pc_image || "" : "",
			nickname: data?.nickname,
			uid: data?.uid,
			area_type: data?.area_type,
		});
	},
	{ immediate: true, deep: true }
);
const profileName = computed(
	() =>
		(locale.value === "en" ? props.data?.nickname_en : props.data?.nickname) ||
		props.data?.nickname ||
		""
);
const avatarSrc = computed(() => props.data?.avatar || defaultAvatar);
const areaHeaderImage = computed(() =>
	isAreaTeacher.value ? props.data?.big_teacher_pc_image || "" : ""
);
const areaMobileHeaderImage = computed(() =>
	isAreaTeacher.value ? props.data?.big_teacher_image || "" : ""
);
const areaDefaultTagStyle = computed(() => {
	if (!isAreaTeacher.value) return {};

	const colors = props.data?.star_teacher_colour || {};
	return {
		color: numberColorToCss(colors.text) || undefined,
		backgroundColor: numberColorToCss(colors.masking) || undefined,
	};
});

const profileTagItems = computed(() =>
	normalizeTagItems(
		locale.value === "en"
			? props.data?.tags_en ||
					props.data?.academic_sector_en ||
					props.data?.label_en ||
					props.data?.tags ||
					props.data?.academic_sector ||
					props.data?.label
			: props.data?.tags || props.data?.academic_sector || props.data?.label
	).slice(0, 4)
);

const profileIntro = computed(
	() =>
		(locale.value === "en"
			? props.data?.introduce_en ||
				props.data?.introduction_en ||
				props.data?.creator_introduction_en
			: props.data?.introduce) ||
		props.data?.introduce ||
		""
);

const displayYear = computed(() => {
	const year = props.data?.year;
	if (!year) return "--";
	if (locale.value === "en") {
		return String(year).replace(/年$/, " years");
	}
	return year;
});
const displayAnswers = computed(() => props.data?.answers ?? "--");
const displayScore = computed(() => props.data?.score || "5.0");
const isAssistantChat = computed(
	() =>
		(isAreaTeacher.value || props.data?.is_big_teacher) && isEnabledFlag(props.data?.is_assistant)
);

const chatTeacherId = computed(() => {
	if (isAreaTeacher.value || props.data?.is_big_teacher) {
		if (isAssistantChat.value) {
			return normalizeChatId(
				props.data?.big_teacher_assistant_str || props.data?.big_teacher_assistant
			);
		}
		if (isEnabledFlag(props.data?.is_free_chat)) {
			return normalizeChatId(props.data?.uid);
		}
		return "";
	}
	return normalizeChatId(props.data?.uid);
});
const chatButtonText = computed(() =>
	isAssistantChat.value ? t(`${I18N_NS}.freeAssistantConsult`) : t(`${I18N_NS}.freeConsult`)
);

function isEnabledFlag(value) {
	return value === true || value === 1 || value === "1" || value === "true";
}

function normalizeChatId(value) {
	if (value === undefined || value === null) return "";
	const normalized = String(value).trim();
	return normalized && normalized !== "0" ? normalized : "";
}

function normalizeTagItems(source) {
	if (Array.isArray(source)) {
		return source
			.filter(Boolean)
			.map(item => normalizeTagItem(item))
			.filter(item => item.text);
	}

	if (typeof source === "string") {
		return source
			.split(/[,，/|]/)
			.map(item => normalizeTagItem(item))
			.filter(item => item.text);
	}

	return [];
}

function normalizeTagItem(item) {
	if (!item) return { text: "" };
	if (typeof item === "object") {
		return {
			text: String(item.text || item.name || item.label || item.title || "").trim(),
			textColor: numberColorToCss(item.text_color ?? item.textColor),
			bgColor: numberColorToCss(item.color ?? item.bg_color ?? item.bgColor),
		};
	}
	return { text: String(item).trim() };
}

function getProfileTagStyle(tag) {
	if (!isAreaTeacher.value) return {};
	return {
		...areaDefaultTagStyle.value,
		color: tag.textColor || areaDefaultTagStyle.value.color,
		backgroundColor: tag.bgColor || areaDefaultTagStyle.value.backgroundColor,
	};
}

function numberColorToCss(value) {
	if (value === undefined || value === null || value === "") return "";
	if (typeof value === "string" && value.trim().startsWith("#")) return value.trim();
	if (typeof value === "string" && value.trim().startsWith("rgb")) return value.trim();

	const numberValue = Number(value);
	if (!Number.isFinite(numberValue) || numberValue < 0) return "";

	const hex = (numberValue >>> 0).toString(16).padStart(8, "0");
	const alpha = parseInt(hex.slice(0, 2), 16) / 255;
	const red = parseInt(hex.slice(2, 4), 16);
	const green = parseInt(hex.slice(4, 6), 16);
	const blue = parseInt(hex.slice(6, 8), 16);

	if (alpha >= 0.999) {
		return `#${hex.slice(2)}`;
	}

	return `rgba(${red}, ${green}, ${blue}, ${alpha.toFixed(3)})`;
}

const toChat = async () => {
	if (import.meta.client) {
		const { logEvent } = await import("~/utils/firebase");
		logEvent("site_masterpage_ask");
	}
	tracking?.track?.("mentor_free_consult_click", {
		cta_position: "header",
	});

	openLogin?.();
	if (!chatTeacherId.value) return;
	await navigateTo(`/seer/chat/${chatTeacherId.value}`);
};

watch(
	() => [hasProfile.value, props.data?.uid, props.data?.score],
	async () => {
		if (!hasProfile.value || !impressionTracker) return;
		await nextTick();
		const mentorId = String(props.data?.uid || "");
		if (profileSectionRef.value) {
			impressionTracker.bind(
				profileSectionRef.value,
				`profile_${mentorId}`,
				"mentor_profile_section_view",
				{
					profile_completeness: tracking.getProfileCompleteness(props.data),
				}
			);
		}
		if (privacyBannerRef.value) {
			impressionTracker.bind(
				privacyBannerRef.value,
				`privacy_${mentorId}`,
				"mentor_privacy_banner_view"
			);
		}
	},
	{ immediate: true }
);

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

.teacher-profile {
	position: relative;
	padding: 44px 48px;
	background: #ffffff;
	border: 1px solid #eeeeee;
	border-radius: 8px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
	font-family: "Source Han Serif CN", "Source Han Serif CN";
	margin-top: 15px;

	&__header {
		display: flex;
		align-items: flex-start;
		gap: 32px;
	}

	&__avatar-wrap {
		flex: 0 0 auto;
		width: 148px;
		height: 148px;
		padding: 4px;
		border-radius: 50%;
		background: #fff7e5;
	}

	&__avatar {
		display: block;
		width: 100%;
		height: 100%;
		border-radius: 50%;
		object-fit: cover;
	}

	&__area-picture {
		display: none;
	}

	&__area-image {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	&__main {
		flex: 1;
		min-width: 0;
		padding-top: 6px;
	}

	&__top {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 24px;
	}

	&__identity {
		flex: 1;
		min-width: 0;
	}

	&__name {
		margin: 0;
		min-width: 0;
		font-size: 24px;
		font-weight: 600;
		line-height: 1.2;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin: 14px 0 0;
		padding: 0;
		list-style: none;
	}

	&__tag {
		max-width: 180px;
		height: 26px;
		padding: 0 10px;
		border-radius: 4px;
		background: rgba(242, 175, 25, 0.1);
		color: #f2af19;
		font-size: 15px;
		font-weight: 500;
		line-height: 26px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__chat-btn {
		flex: 0 0 auto;
		width: 228px;
		height: 48px;
		border: 0;
		border-radius: 4px;
		background: #333333;
		color: #ffffff;
		font-size: 20px;
		font-weight: 600;
		cursor: pointer;
		transition: opacity 0.2s ease;

		&:hover {
			opacity: 0.7;
		}
	}

	&__stats {
		display: flex;
		flex-wrap: wrap;
		gap: 28px;
		margin: 24px 0 0;

		&--spacer {
			min-height: 52px;
		}
	}

	&__stat {
		display: flex;
		flex-direction: column-reverse;
		// align-items: center;
		min-width: 92px;
		margin: 0;

		dt {
			margin-top: 8px;
			color: #595959;
			font-size: 16px;
			line-height: 1.2;
		}

		dd {
			margin: 0;
			color: #333333;
			font-size: 20px;
			font-weight: 700;
			line-height: 1.2;
		}
	}

	&__intro {
		margin: 30px 0 0;
		color: #333333;
		font-size: 16px;
		line-height: 1.5;
		word-break: break-word;
		overflow-wrap: anywhere;
	}

	&__privacy {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 24px;
		margin-top: 28px;
		padding: 18px 28px;
		border-radius: 8px;
		background: #eef5ff;
		overflow: hidden;
	}

	&__privacy-text {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 6px;
		min-width: 0;
		margin-right: clamp(140px, 16vw, 280px);

		strong {
			color: #1c6ee8;
			font-size: 24px;
			font-weight: 700;
			line-height: 1.2;
		}

		span {
			display: block;
			color: #333333;
			font-size: 24px;
			line-height: 1.45;
			word-break: break-word;
		}
	}

	&__privacy-check {
		position: absolute;
		right: -10px;
		bottom: 0;
		width: auto;
		height: 100%;
	}

	&--area {
		padding: 0;
		overflow: hidden;
		background: #ffffff;

		.teacher-profile__header {
			position: relative;
			display: grid;
			align-items: center;
			min-height: 278px;
			margin: 48px 48px 0;
			padding: 32px clamp(24px, 5vw, 72px) 32px clamp(280px, 34%, 620px);
			overflow: hidden;
		}

		.teacher-profile__main {
			position: relative;
			z-index: 1;
			min-width: 0;
			max-width: none;
			padding-top: 0;
			overflow: visible;
		}

		.teacher-profile__area-picture {
			display: block;
			position: absolute;
			inset: 0;
			z-index: 0;
			width: 100%;
			height: 100%;
			border-radius: 6px;
			overflow: hidden;
		}

		.teacher-profile__top {
			align-items: flex-start;
		}

		.teacher-profile__name {
			font-size: 24px;
		}

		.teacher-profile__tags {
			margin-top: 12px;
			gap: 10px;
		}

		.teacher-profile__tag {
			max-width: 220px;
			height: 28px;
			padding: 0 12px;
			border-radius: 4px;
			background: rgba(242, 175, 25, 0.1);
			color: #f2af19;
			font-size: 15px;
			line-height: 28px;
		}

		.teacher-profile__intro {
			display: -webkit-box;
			margin: 20px 0 0;
			max-width: 720px;
			font-size: 16px;
			line-height: 1.5;
			overflow: hidden;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 4;
		}

		.teacher-profile__chat-btn {
			width: 180px;
			height: 42px;
			font-size: 16px;
		}

		.teacher-profile__privacy {
			margin: 34px 48px 46px;
		}
	}
}

@media (max-width: 1280px) {
	.teacher-profile {
		&__privacy-text {
			strong {
				font-size: 32px;
			}

			span {
				font-size: 24px;
			}
		}

		&--area {
			.teacher-profile__header {
				min-height: 246px;
				margin: 36px 36px 0;
				padding: 24px 24px 24px clamp(230px, 32%, 360px);
			}

			.teacher-profile__main {
				padding: 0;
			}

			.teacher-profile__name {
				font-size: 24px;
			}

			.teacher-profile__top {
				flex-wrap: wrap;
				gap: 14px;
			}

			.teacher-profile__tag {
				height: 24px;
				font-size: 13px;
				line-height: 24px;
			}

			.teacher-profile__intro {
				margin-top: 14px;
				font-size: 15px;
				line-height: 1.5;
				-webkit-line-clamp: 4;
			}

			.teacher-profile__chat-btn {
				width: 150px;
				height: 38px;
				font-size: 14px;
			}
		}
	}
}

@media (max-width: 900px) {
	.teacher-profile {
		&--area {
			.teacher-profile__header {
				display: block;
				position: relative;
				min-height: 0;
				margin: 24px 24px 0;
				padding: 0;
				aspect-ratio: 686 / 306;
				border: 1px solid #eeeeee;
				border-radius: 8px;
				background: #f8f8f8;
				overflow: hidden;
			}

			.teacher-profile__main {
				position: absolute;
				top: 12%;
				right: 4%;
				bottom: 8%;
				left: 42%;
				display: flex;
				flex-direction: column;
				justify-content: center;
				width: auto;
				padding: 0;
				overflow: hidden;
			}

			.teacher-profile__area-picture {
				position: absolute;
				inset: 0;
				width: 100%;
				height: 100%;
				border-radius: 0;
			}

			.teacher-profile__area-image {
				position: absolute;
				inset: 0;
			}

			.teacher-profile__top {
				flex-wrap: nowrap;
				gap: 12px;
				padding: 0;
			}

			.teacher-profile__name {
				font-size: 24px;
				white-space: normal;
				display: -webkit-box;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical;
			}

			.teacher-profile__tags {
				margin-top: 8px;
				gap: 6px;
			}

			.teacher-profile__tag {
				max-width: 160px;
				height: 22px;
				padding: 0 8px;
				font-size: 12px;
				line-height: 22px;
			}

			.teacher-profile__chat-btn {
				width: auto;
				min-width: 126px;
				height: 36px;
				padding: 0 14px;
				font-size: 14px;
			}

			.teacher-profile__intro {
				display: -webkit-box;
				margin: 12px 0 0;
				max-width: none;
				font-size: 15px;
				line-height: 1.55;
				overflow: hidden;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 4;
			}

			.teacher-profile__privacy {
				margin: 20px 24px 28px;
			}
		}
	}
}

@media (max-width: 900px) {
	.teacher-profile {
		padding: 34px 24px 28px;

		&__name {
			font-size: 28px;
		}

		&__intro {
			font-size: 17px;
			line-height: 1.55;
		}

		&__privacy {
			padding: 16px 20px;
		}

		&__privacy-text {
			strong {
				font-size: 28px;
			}

			span {
				font-size: 20px;
			}
		}

		&__header,
		&__top {
			flex-wrap: wrap;
		}

		&__chat-btn {
			width: min(100%, 228px);
		}

		&--area {
			padding: 0;
		}
	}
}

@media (max-width: 640px) {
	.teacher-profile {
		&__name {
			font-size: 24px;
		}

		&__tag {
			max-width: 140px;
			height: 24px;
			font-size: 13px;
			line-height: 24px;
		}

		&__stat {
			min-width: 76px;

			dt {
				font-size: 13px;
			}

			dd {
				font-size: 22px;
			}
		}

		&__intro {
			font-size: 15px;
			line-height: 1.55;
		}

		&__privacy {
			padding: 14px 16px;
		}

		&__privacy-text {
			strong {
				font-size: 24px;
			}

			span {
				font-size: 16px;
			}
		}

		&--area {
			.teacher-profile__header {
				margin: 16px 16px 0;
				padding: 0;
			}

			.teacher-profile__main {
				padding: 0;
			}

			.teacher-profile__name {
				font-size: 20px;
			}

			.teacher-profile__tag {
				max-width: 120px;
				height: 20px;
				font-size: 11px;
				line-height: 20px;
			}

			.teacher-profile__chat-btn {
				width: auto;
				min-width: 92px;
				height: 32px;
				padding: 0 10px;
				font-size: 12px;
			}

			.teacher-profile__intro {
				margin: 10px 14px 16px;
				font-size: 13px;
				line-height: 1.5;
				-webkit-line-clamp: 4;
			}

			.teacher-profile__privacy {
				margin: 16px 16px 24px;
			}
		}
	}
}
</style>
